import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT, documentSectionTitleSx } from '../constants/documentTheme';
import { partnerOutlineBtnSx, partnerOxbloodBtnSx } from './PartnerListChrome';

const SEVERITY_ICON = {
  critical: ErrorOutlineIcon,
  urgent: PriorityHighIcon,
  overdue: WarningAmberOutlinedIcon,
  warning: WarningAmberOutlinedIcon,
  info: InfoOutlinedIcon,
  opportunity: LightbulbOutlinedIcon,
};

const SEVERITY_DOT = {
  critical: '#B71C1C',
  urgent: '#C62828',
  overdue: '#E65100',
  warning: '#F9A825',
  info: '#1565C0',
  opportunity: '#6D4C41',
};

const FILTER_OPTIONS = [
  { id: 'all' },
  { id: 'unassigned' },
  { id: 'urgent' },
  { id: 'overdue' },
  { id: 'warning' },
  { id: 'info' },
  { id: 'opportunity' },
  { id: 'flight' },
  { id: 'request' },
  { id: 'inquiry' },
];

function firstName(name) {
  if (!name) return '';
  return String(name).trim().split(/\s+/)[0] || name;
}

function summarizeAttention(items = []) {
  const open = items.length;
  const urgent = items.filter((item) => item.severity === 'urgent' || item.severity === 'critical').length;
  const overdue = items.filter((item) => item.severity === 'overdue').length;
  return { open, urgent, overdue };
}

const PartnerNeedsAttention = ({
  items = [],
  loading = false,
  onView,
  onAssign,
  onChanged,
  sectionHeadSx,
}) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [resolveItem, setResolveItem] = useState(null);
  const [resolution, setResolution] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState('');

  const summary = useMemo(() => summarizeAttention(items), [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (filter === 'all') return true;
      if (filter === 'unassigned') return Boolean(item.unassigned);
      if (filter === 'request') return item.kind === 'request' || Boolean(item.requestId);
      if (filter === 'flight') return item.kind === 'flight' || item.type === 'flight';
      if (filter === 'inquiry') return item.kind === 'inquiry' || Boolean(item.inquiryId);
      if (['urgent', 'overdue', 'warning', 'info', 'opportunity', 'critical'].includes(filter)) {
        if (filter === 'urgent') return item.severity === 'urgent' || item.severity === 'critical';
        return item.severity === filter;
      }
      return true;
    });
  }, [items, filter]);

  const handleResolve = async () => {
    if (!resolveItem?.requestId) return;
    try {
      setResolving(true);
      setResolveError('');
      await api.post(
        `/partners/requests/${resolveItem.requestId}/resolve`,
        { resolution: resolution.trim() || undefined },
        { usePartnerAuth: true }
      );
      setResolveItem(null);
      setResolution('');
      onChanged?.();
    } catch (err) {
      setResolveError(err.response?.data?.error || t('partnerOps.actionFailed'));
    } finally {
      setResolving(false);
    }
  };

  const renderActions = (item) => {
    const isInquiry = item.kind === 'inquiry' || Boolean(item.inquiryId);
    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
        <Button size="small" sx={partnerOutlineBtnSx} onClick={() => onView?.(item)}>
          {t('partnerOps.view')}
        </Button>
        {!isInquiry ? (
          <Button size="small" sx={partnerOutlineBtnSx} onClick={() => onAssign?.(item)}>
            {t('partnerOps.assign')}
          </Button>
        ) : null}
        {item.requestId ? (
          <Button
            size="small"
            sx={partnerOutlineBtnSx}
            onClick={() => {
              setResolveItem(item);
              setResolution('');
              setResolveError('');
            }}
          >
            {t('partnerOps.resolve')}
          </Button>
        ) : null}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 4 }} data-testid="partner-needs-attention">
      <Box
        sx={{
          ...sectionHeadSx,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography component="h2" sx={documentSectionTitleSx}>
            {t('partnerOps.needsAttentionTitle')}
          </Typography>
          {!loading ? (
            <Typography
              data-testid="partner-needs-attention-summary"
              sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.5 }}
            >
              {t('partnerOps.needsAttentionSummary', {
                open: summary.open,
                urgent: summary.urgent,
                overdue: summary.overdue,
              })}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
              {t('partnerOps.needsAttentionSub')}
            </Typography>
          )}
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            displayEmpty
            data-testid="partner-needs-attention-filter"
            sx={{ borderRadius: 0, bgcolor: DOCUMENT.surface, fontSize: 13 }}
          >
            {FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {t(`partnerOps.attentionFilter.${option.id}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ p: 2, border: `1px solid ${DOCUMENT.ruleHard}`, borderTop: 0, bgcolor: DOCUMENT.surface }}>
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink3 }}>{t('common.loading')}</Typography>
        </Box>
      ) : !items.length ? (
        <Box
          data-testid="partner-needs-attention-empty"
          sx={{ p: 2.5, border: `1px solid ${DOCUMENT.ruleHard}`, borderTop: 0, bgcolor: DOCUMENT.surface }}
        >
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
            {t('partnerOps.needsAttentionEmpty')}
          </Typography>
        </Box>
      ) : !filteredItems.length ? (
        <Box sx={{ p: 2.5, border: `1px solid ${DOCUMENT.ruleHard}`, borderTop: 0, bgcolor: DOCUMENT.surface }}>
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
            {t('partnerOps.needsAttentionFilterEmpty')}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            border: `1px solid ${DOCUMENT.ruleHard}`,
            borderTop: 0,
            bgcolor: DOCUMENT.surface,
          }}
        >
          {filteredItems.map((item) => {
            const Icon = SEVERITY_ICON[item.severity] || InfoOutlinedIcon;
            const typeLabel = t(`partnerOps.requestType.${item.type}`, { defaultValue: item.type });
            const client = firstName(item.clientName) || item.clientName || '—';
            const place = item.destination || item.tripTitle || '—';
            return (
              <Box
                key={item.id}
                data-testid={`partner-attention-row-${item.id}`}
                sx={{
                  display: { xs: 'block', md: 'grid' },
                  gridTemplateColumns: { md: 'minmax(0, 1fr) auto' },
                  gap: { md: 2 },
                  alignItems: 'center',
                  px: 2,
                  py: 1.75,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                  '&:last-child': { borderBottom: 0 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                  <Box
                    aria-hidden
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: SEVERITY_DOT[item.severity] || DOCUMENT.ink3,
                      mt: 0.7,
                      flexShrink: 0,
                    }}
                  />
                  <Icon sx={{ fontSize: 16, color: DOCUMENT.ink2, mt: 0.35, display: { xs: 'none', sm: 'block' } }} aria-hidden />
                  <Typography sx={{ fontSize: 14, color: DOCUMENT.ink, lineHeight: 1.45 }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {typeLabel}
                    </Box>
                    {' — '}
                    {client}
                    {' / '}
                    {place}
                    {' — '}
                    {item.issue}
                  </Typography>
                </Box>
                <Box sx={{ mt: { xs: 1.25, md: 0 } }}>{renderActions(item)}</Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog open={Boolean(resolveItem)} onClose={() => setResolveItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400 }}>
          {t('partnerOps.actions.resolve')}
        </DialogTitle>
        <DialogContent>
          {resolveError ? (
            <Typography sx={{ color: DOCUMENT.oxblood, fontSize: 13, mb: 1 }}>{resolveError}</Typography>
          ) : null}
          <TextField
            fullWidth
            multiline
            minRows={3}
            autoFocus
            value={resolution}
            onChange={(event) => setResolution(event.target.value)}
            placeholder={t('partnerOps.resolvePlaceholder')}
            sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResolveItem(null)} sx={partnerOutlineBtnSx}>
            {t('partnerLists.cancel')}
          </Button>
          <Button onClick={handleResolve} disabled={resolving} sx={partnerOxbloodBtnSx}>
            {t('partnerOps.actions.resolve')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerNeedsAttention;
